import { FunctionComponent, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import * as eventApi from '../api/EventLog';
import { IEventLogRow } from '../util/DataModelInterface';
import { DataTable } from './DataTable';
import { Spinner } from './Spinner';
import { IFilterButtonData } from './TableFilters';
import { DateSelector } from './DateSelector';
import useToastHandler from '../hooks/useToastHandler';

interface IEventLogProps {
}

interface IEventTableData {
  date: string,
  user: string,
  company: string,
  type: string,
  event: string,
  info: string
}

export const EventLog: FunctionComponent<IEventLogProps> = (props) => {
    const [logData, setLogData] = useState([] as IEventLogRow[]);
    const [logRowData, setLogRowData] = useState([] as IEventTableData[]);
    const [loading, setLoading] = useState(false);
    const [inputStartValue, setInputStartValue] = useState<string>('');
    const [inputStopValue, setInputStopValue] = useState<string>('');
    const [filters, setFilters] = useState([] as IFilterButtonData[]);

    const intl = useIntl();
    const { addToast } = useToastHandler();

    useEffect(() => {
      setLoading(true);
      eventApi.readLog().then((data: any) => {
          // console.log("Event Log - data", data);
          if (data?.error) {
            addToast(data.error, 'log', 'fetch');
          } else if (data) {
            setLogData(data);
            setEventTableData(data);
          }
      }).finally(() => {
        setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
      if (inputStopValue) {
        setLoading(true);
        eventApi.readLog(inputStartValue, inputStopValue).then((data: any) => {
          // console.log("Event Log - data", data);
          if (data?.error) {
            addToast(data.error, 'log', 'fetch');
          } else if (data) {
            setLogData(data);
            setEventTableData(data);
          }
        }).finally(() => {
            setLoading(false);
        });
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inputStartValue, inputStopValue]);

    const exportLog = async () => {
      eventApi.exportLog("",
          {
            header: intl.formatMessage({id: 'eventlog.header'}),
            data: logData
          }
      ).then((data: any) => {
          if (data?.error) {
            addToast(data.error, 'log', 'fetch');
          }
      })
    }

    const setEventTableData = (eventLogData: any[]) => {
      let data = [] as IEventTableData[];
      let allEvents = 0;
      let reportingEvents = 0;
      let userEvents = 0;
      let companyEvents = 0;
      eventLogData.forEach((r, i) => {
        data[i] = {
          date: r.timestamp ? r.timestamp.replace(/T|[.].+/g, " ") : "",
          user: r.user_name,
          company: r.company,
          type: r.type,
          event: r.event,
          info: r.info
        };
        if (r.type === 'reporting') reportingEvents++;
        if (r.type === 'user') userEvents++;
        if (r.type === 'company') companyEvents++;
      });
      allEvents = data.length;
      setFilters([{ button: 'all', count: allEvents, 
                    filters: [{column: 'type', filterValues: ['']}] },
                  { button: 'reporting', count: reportingEvents, 
                    filters: [{column: 'type', filterValues: ['reporting']}] },
                  { button: 'user', count: userEvents, 
                    filters: [{column: 'type', filterValues: ['user']}] },
                  { button: 'company', count: companyEvents, 
                    filters: [{column: 'type', filterValues: ['company']}] }]);
      setLogRowData(data);
    }

    const toolBar = () => {
      return(
      <div className="toolbar">
        <span className="item-actions ms-auto">
          <button key='toolbarbutton' className="btn btn btn-primary btn-reminder"
            disabled={logData.length === 0}
            onClick={() => exportLog()}>
            <i className="me-2 fa fa-download" aria-hidden="true"></i>
            <FormattedMessage id={'button.report.download'}/>
          </button>
        </span>
      </div>);
    }

    return (
      <>
        <Spinner active={loading} />
        <div className="d-flex flex-md-row flex-column">
            <div id="history" className="flex-fill">
              <h2><FormattedMessage id={'eventlog.title'}/></h2>

              <DateSelector
                startPeriod={inputStartValue}
                endPeriod={inputStopValue}
                isStartAndEnd={true}
                onChange={(start, end) => { setInputStartValue(start); setInputStopValue(end);}}
                showMonth={false}
                dateFormat='yyyy-MM-dd'
                inputFormat='y-MM-dd'/>

              <DataTable
                tableheadings={['date', 'user', 'company', 'type', 'event', 'info']}
                filters={filters}
                tabledata={logRowData} tableType='eventlog' tableId='historytable'
                toolbar={toolBar()}
                showFilters={true}
              />
            </div>
          </div>
      </>
    );
};